import React, { useMemo, useState } from 'react';
import { Button, Drawer, Grid, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import { useCompaniesData } from '../../../../../../Hooks/TT_ELD/Companies';
import SearchInput from '../../../../../../Utils/SearchInput';
import SearchOptions from '../../../../../../Utils/SearchOptions';
import { SearchForOwnerDispatchers } from '../../../../../../Utils/SearchResults';
import CompaniesTable from '../CompaniesTable';

const { useBreakpoint } = Grid;

type DisabledCompaniesProps = {
  companyName: string;
  setCompanyName: (value: string) => void;
  usdotId: string;
  setUsdotId: (value: string) => void;
  uid: string;
  setUid: (value: string) => void;
  skip: string;
  setSkip: (value: string) => void;
};

const DisabledCompanies = ({
  companyName,
  setCompanyName,
  usdotId,
  setUsdotId,
  uid,
  setUid,
  skip,
  setSkip,
}: DisabledCompaniesProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [ownerId, setOwnerId] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      companyName: setCompanyName,
      usdotId: setUsdotId,
      uid: setUid,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
      setSkip('1');
    }
  };

  const { data, refetch, isLoading, isFetching } = useCompaniesData(
    companyName,
    usdotId,
    undefined,
    skip,
    'true',
    ownerId,
    '',
    uid,
  );

  const onChange = (query: any) => setSkip(query.current);

  const refresh = () => refetch();

  const handleOwnerSelect = (value: any, { valId }: { valId: any }) => {
    setOwnerId(valId);
    setSkip('1');
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          value={companyName}
          placeholder="Search by company"
          onDebounceChange={(event: string) => handleChange(event, 'companyName')}
          searchWidth="100%"
        />

        <SearchInput
          value={usdotId}
          placeholder="Search by USDOT"
          onDebounceChange={(event: string) => handleChange(event, 'usdotId')}
          searchWidth="100%"
        />

        <SearchOptions
          SearchResult={SearchForOwnerDispatchers}
          onSelect={handleOwnerSelect}
          placeholder="Search by owner"
          searchWidth="100%"
        />

        <SearchInput
          value={uid}
          placeholder="Search by UID"
          onDebounceChange={(event: string) => handleChange(event, 'uid')}
          searchWidth="100%"
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refresh();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [companyName, usdotId, uid, isMobile],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <SearchInput
            value={companyName}
            placeholder="Search by company"
            onDebounceChange={(event: string) => handleChange(event, 'companyName')}
          />

          <SearchInput
            value={usdotId}
            placeholder="Search by USDOT"
            onDebounceChange={(event: string) => handleChange(event, 'usdotId')}
          />

          <SearchOptions
            SearchResult={SearchForOwnerDispatchers}
            onSelect={handleOwnerSelect}
            placeholder="Search by owner"
          />

          <SearchInput
            value={uid}
            placeholder="Search by UID"
            onDebounceChange={(event: string) => handleChange(event, 'uid')}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <Button size="large" onClick={refresh}>
              Refresh
            </Button>
          </div>
        </div>
      ) : (
        // Mobile: Filter + Refresh
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block size="large" onClick={refresh}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <CompaniesTable
        companiesData={data?.data}
        total={data?.count}
        onChange={onChange}
        isLoading={isLoading}
        isFetching={isFetching}
        skip={skip}
        refetch={refetch}
      />
    </div>
  );
};

export default DisabledCompanies;
